// React imports
import { useEffect } from 'react';

// MUI imports
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

// Generic imports
import {parse} from 'csv-parse/browser/esm/sync';
import _ from 'lodash';

// Local imports
import { matchPort, productToMonada, datePartsToString } from './RatesConvert.utils.js'

function RatesConvertZIM(props) {
    const { data, formObj, supplier, supplierEmail, seaPorts, onDone } = props;

    useEffect(() => {
        (async () => {
            const result = await convert({ data, supplier, supplierEmail, formObj, seaPorts });
            onDone(result);
        })();
    }, []);

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '24px' }}>
            <CircularProgress size={36} />
        </Box>
    );
}

export default RatesConvertZIM;

async function convert({ data, formObj, supplier, supplierEmail, seaPorts }) {
    const csv = parse(data, { columns: true, });

    const routeRates = {};

    const rates = [];
    // Iterate through each records
    for await (const record of csv) {
        const source = matchPort({ name: record.POL, seaPorts });
        const destination = matchPort({ name: record.POD, seaPorts });
        const transportationMethod = 'sea';

        if (!source || !destination) {
            throw new Error(`error in source & destination in ${JSON.stringify(record)}`);
        }

        const validFrom = record['Valid From'].split('.');
        const validUntil = record['Valid To'].split('.');
        const transitTime = parseInt(record['Estimated Transit Time'] || record['Est. T/T (Days)']);

        const values = _.compact(_(record).omit(['Estimated Transit Time', 'Est. T/T (Days)', 'Valid From', 'Valid To', 'Origin' ,'Destination', 'Type/Size', 'Dangerous?', 'HAZ', 'Freight Rate', 'Valid From', 'Valid To', 'Remarks']).values().join('/').split('/').map(f => {
            const s = f.match(/^(\w*)\((\d*),(\w*),(\w*)\)$/);
            if (!s) return null;
            return { title: s[1], price: s[2], currency: s[3], type: s[4] };
        }));

        const parsedValues = {};


        formObj.sections.forEach(s => {
            const ret = s.fields.forEach(f => {
                let v = values.find(v => f.title === v.title);
                if (v) {
                    if (v.type === 'BL') {
                        const key = [source.code, destination.code, transportationMethod, record['HAZ'], `${validFrom[2]}-${validFrom[1]}-${validFrom[0]}`, `${validUntil[2]}-${validUntil[1]}-${validUntil[0]}`, transitTime].join(':');
                        routeRates[key] = routeRates[key] || {};
                        routeRates[key][f.title] = `${parseFloat(v.price)}|${v.currency}`;
                    } else {
                        parsedValues[f.title] = `${parseFloat(v.price)}|${v.currency}`;
                    }
                } else {
                    v = fieldFromRecord({ field: f.title, record });
                    if (v) {
                        if (f.type === 'number') {
                            parsedValues[f.title] = `${parseFloat(v)}|${formObj.currencies[0]}`;
                        } else {
                            parsedValues[f.title] = v;
                        }
                    } else {
                        if (f.type === 'number') {
                            parsedValues[f.title] = '';
                        } else {
                            parsedValues[f.title] = '';
                        }
                    }
                }
            });
            return (s.type === 'peritem') ? [ret] : ret;
        });

        rates.push({
            supplier,
            supplierEmail,
            form: formObj.title,
            transportationMethod,
            source: source.code,
            destination: destination.code,
            product: productToMonada(record['Type/Size']),
            dangerous: (record['Dangerous?'] || record['HAZ'])  === 'Y',
            validFrom: datePartsToString(validFrom),
            validUntil: datePartsToString(validUntil),
            transitTime,
            ...parsedValues,
        })
    }

    _.forEach(routeRates, (v, r) => 
        rates.push({
            supplier,
            supplierEmail,
            form: formObj.title,
            transportationMethod: r.split(':')[2],
            source: r.split(':')[0],
            destination: r.split(':')[1],
            product: 'route',
            dangerous: r.split(':')[3]  === 'Y',
            validFrom: r.split(':')[4],
            validUntil: r.split(':')[5],
            transitTime: r.split(':')[6],
            ...v,
        })
    );

    return rates;
}

function fieldFromRecord({ field, record }) {
    const name = {
        'Freight Rate': 'FRT',
        'Remarks': 'Notes',
    }[field] || field;

    return record[name];
}
